/**
 * Delete Member Dialog Component
 * Confirmation dialog for permanently deleting an inactive member
 */

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import type { Member } from '@/types/api';

interface DeleteMemberDialogProps {
  member: Member | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => void;
  isDeleting?: boolean;
}

/**
 * DeleteMemberDialog - Confirmation for permanent member deletion
 */
export function DeleteMemberDialog({
  member,
  open,
  onOpenChange,
  onConfirm,
  isDeleting = false,
}: DeleteMemberDialogProps) {
  if (!member) return null;

  return (
    <AlertDialog
      open={open}
      onOpenChange={(nextOpen) => {
        // Keep the dialog up while the delete is in flight (Esc/overlay included)
        if (!isDeleting) onOpenChange(nextOpen);
      }}
    >
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Delete Member Permanently</AlertDialogTitle>
          {/* AlertDialogDescription renders a <p>; keep block elements outside it */}
          <AlertDialogDescription>
            Are you sure you want to permanently delete this member?
          </AlertDialogDescription>
          <div className="bg-muted p-3 rounded-md mt-2">
            <p className="font-medium">{member.name}</p>
            <p className="text-sm text-muted-foreground">
              ITS ID: {member.id}
              {member.thaaliNumber ? ` · Thaali #${member.thaaliNumber}` : ''}
            </p>
          </div>
          <p className="text-sm text-destructive mt-2">
            Their RSVP and feedback history will also be deleted. This action
            cannot be undone.
          </p>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={(event) => {
              // AlertDialogAction is a Radix Close button; without this the
              // dialog dismisses before the DELETE request resolves.
              event.preventDefault();
              onConfirm();
            }}
            disabled={isDeleting}
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
          >
            {isDeleting ? 'Deleting...' : 'Delete Permanently'}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
